import api from "@/app/api";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { toGuidMap } from "@/app/apiutils";
import type BaseEnumType from "@/app/services/baseEnumType";
import type { ActivityLogEntry } from "@/app/services/generated/activitylogService";
import type { BmTask } from "@/app/services/generated/bmtaskService";
import { bmTaskService } from "@/app/services/generated/bmtaskService";
import type { CommunicationSubject } from "@/app/services/generated/communicationsubjectService";
import type { CommunicationType } from "@/app/services/generated/communicationtypeService";
import type { Credit } from "@/app/services/generated/creditService";
import { creditService } from "@/app/services/generated/creditService";
import { EnumActivityLogType } from "@/app/services/generated/enums/enumActivityLogType.generated";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import { EnumCustomerHappinessRating } from "@/app/services/generated/enums/enumCustomerHappinessRating.generated";
import type { Order } from "@/app/services/generated/orderService";
import type { OutletCommunicationLogEntry } from "@/app/services/generated/outletcommunicationlogService";
import { outletCommunicationLogService } from "@/app/services/generated/outletcommunicationlogService";
import type { User } from "@/app/services/generated/userService";
import type Guid from "@/app/types/common/guid";
import { GuidMap, GuidSet } from "@/app/types/common/guidcollections";
import { mainMenu } from "@/app/ui/navigation/mainMenu";
import { salesMenu } from "@/app/ui/navigation/salesMenu";
import { translate, useI18n } from "@/plugins/i18n";
import type { Location } from "vue-router";
import { orderService } from "@/app/services/extensions/orderServiceExtended";
import { userService } from "@/app/services/extensions/userServiceExtended";
import type { LeadCommunicationLogEntry } from "@/app/services/generated/leadcommunicationlogService";
import { leadCommunicationLogService } from "@/app/services/generated/leadcommunicationlogService";
import CommunicationTypesCache from "@/app/caching/caches/communicationTypesCache";
import CommunicationSubjectsCache from "@/app/caching/caches/communicationSubjectsCache";
import apiCache from "@/app/caching/apiCache";
import Key from "@/app/types/common/key";
import type { SupplierManufacturerCommunicationLogEntry } from "@/app/services/generated/suppliermanufacturercommunicationlogService";
import { supplierManufacturerCommunicationLogService } from "@/app/services/generated/suppliermanufacturercommunicationlogService";

const { $t, $m } = useI18n();

interface ActivityAssociationLine {
  name?: string; // Appears in Bold:
  value: string;
}
interface ActivityAssociation {
  textLines: ActivityAssociationLine[];
  route?: Location;
  name?: string;
  icon?: string;
}

export interface ActivityLogCardType {
  refresh: () => Promise<void>;
}

export interface ActivityLogEntryCardType {
  timestampString: string;
  taskType: string;
  entityType: string;
  status: EnumLogEntryStatus | null;
  association: ActivityAssociation | null;
}

function getActivityLogStatus(logEntry: ActivityLogEntry) {
  switch (logEntry.activityLogType) {
    case EnumActivityLogType.creditCreated:
    case EnumActivityLogType.orderCreated:
    case EnumActivityLogType.taskCreated:
      return EnumLogEntryStatus.created;

    case EnumActivityLogType.creditCancelled:
    case EnumActivityLogType.orderCancelled:
    case EnumActivityLogType.taskCancelled:
      return EnumLogEntryStatus.cancelled;

    case EnumActivityLogType.creditCompleted:
    case EnumActivityLogType.orderCompleted:
    case EnumActivityLogType.taskCompleted:
      return EnumLogEntryStatus.completed;

    default:
      return null;
  }
}

function mapTaskToActivityAssociation(
  task: BmTask,
  users: GuidMap<User>,
  status: EnumLogEntryStatus | null
): ActivityAssociation {
  const textLines = [];
  if (status === EnumLogEntryStatus.created) {
    let dueDateTime = "";
    const assignedTo = task.assignedToUserId
      ? users.get(task.assignedToUserId)?.name ?? ""
      : $m.none;

    if (task.dueDate) {
      dueDateTime += " " + tenantService.formatDate(task.dueDate);
    }

    if (task.dueTime) {
      dueDateTime += " " + tenantService.formatTime(task.dueTime);
    }

    textLines.push(
      { name: $m.tasks_due.toString(), value: dueDateTime },
      { name: $m.tasks_assigned_to, value: assignedTo },
      { value: task.description },
      {
        name: $m.created_by,
        value: users.get(task.readOnly.createdByUserId)!.name,
      }
    );
  }

  if (status === EnumLogEntryStatus.cancelled) {
    textLines.push({
      name: $m.cancelled_by,
      value: users.get(task.readOnly.cancelledByUserId!)!.name,
    });
  }

  if (status === EnumLogEntryStatus.completed) {
    textLines.push({
      name: $m.completed_by,
      value: users.get(task.readOnly.completedByUserId!)!.name,
    });
  }

  return {
    textLines,
    icon: "format_list_bulleted",
    route: { name: mainMenu.tasks },
    name: $m.tasks,
  };
}

function getIconForHappinessRating(rating: EnumCustomerHappinessRating) {
  switch (rating) {
    case EnumCustomerHappinessRating.negative:
      return "sentiment_very_dissatisfied";

    case EnumCustomerHappinessRating.neutral:
      return "sentiment_neutral";

    case EnumCustomerHappinessRating.positive:
      return "sentiment_very_satisfied";
  }
}

function mapCommunicationLogToAssociation(
  log:
    | OutletCommunicationLogEntry
    | LeadCommunicationLogEntry
    | SupplierManufacturerCommunicationLogEntry,
  users: GuidMap<User>,
  types: GuidMap<CommunicationType>,
  subjects: GuidMap<CommunicationSubject>
): ActivityAssociation {
  const textLines = [];
  textLines.push(
    {
      name: $m.crm_communication_type,
      value: types.get(log.communicationTypeId)!.name,
    },
    {
      name: $m.crm_subject,
      value: subjects.get(log.communicationSubjectId)!.name,
    },
    { value: log.description },
    {
      name: $m.created_by,
      value: users.get(log.userId)!.name,
    }
  );
  return {
    textLines,
    icon: getIconForHappinessRating(log.customerHappinessRating),
  };
}

function mapCreditToAssociation(
  credit: Credit,
  users: GuidMap<User>,
  status: EnumLogEntryStatus | null
): ActivityAssociation {
  const textLines = [];
  if (status === EnumLogEntryStatus.completed) {
    const creditTotal = credit.lines.reduce(
      (acc, line) =>
        acc +
        (line.readOnly?.lineNetPrice ?? 0) +
        (line.readOnly?.lineVat ?? 0),
      0
    );
    textLines.push({
      name: $m.price_total,
      value: tenantService.formatCurrency(creditTotal),
    });
  }
  if (status === EnumLogEntryStatus.cancelled) {
    textLines.push({
      name: $m.cancelled_by,
      value: users.get(credit.header.readOnly!.cancelledByUserId!)!.name,
    });
  }
  return {
    textLines,
    icon: "shopping_basket",
    route: salesMenu.viewCreditNote(credit.header.readOnly!.id),
    name: credit.header.readOnly!.creditNumber,
  };
}

function mapOrderToAssociation(
  order: Order,
  users: GuidMap<User>,
  status: EnumLogEntryStatus | null
): ActivityAssociation {
  const textLines = [];
  if (status === EnumLogEntryStatus.completed) {
    const orderTotal = order.lines.reduce(
      (acc, line) => acc + line.readOnly.lineNetPrice + line.readOnly.lineVat,
      0
    );
    textLines.push({
      name: $m.price_total,
      value: tenantService.formatCurrency(orderTotal),
    });
  }
  if (status === EnumLogEntryStatus.cancelled) {
    textLines.push({
      name: $m.cancelled_by,
      value: users.get(order.header.readOnly!.cancelledByUserId!)!.name,
    });
  }
  return {
    textLines,
    icon: "shopping_basket",
    route: salesMenu.viewOrder(order.header.readOnly!.id),
    name: order.header.readOnly!.orderNumber,
  };
}

const cache = apiCache.buildCacheSet({
  communicationTypes: CommunicationTypesCache,
  communicationSubjects: CommunicationSubjectsCache,
});

export async function fetchActivityLogDependencies(
  activityLogEntries: ActivityLogEntry[]
) {
  const taskIds = new GuidSet();
  const outletCommunicationLogIds = new GuidSet();
  const leadCommunicationLogIds = new GuidSet();
  const creditIds = new GuidSet();
  const orderIds = new GuidSet();
  const supplierManufacturerCommunicationLogIds = new GuidSet();

  activityLogEntries.forEach((entry) => {
    if (!entry.relatedBmEntityId) {
      return;
    }

    switch (entry.relatedBmEntityType) {
      case EnumBmEntityType.bmTask:
        taskIds.add(entry.relatedBmEntityId);
        break;
      case EnumBmEntityType.communicationLogEntry:
        outletCommunicationLogIds.add(entry.relatedBmEntityId);
        break;
      case EnumBmEntityType.leadCommunicationLogEntry:
        leadCommunicationLogIds.add(entry.relatedBmEntityId);
        break;
      case EnumBmEntityType.credit:
        creditIds.add(entry.relatedBmEntityId);
        break;
      case EnumBmEntityType.order:
        orderIds.add(entry.relatedBmEntityId);
        break;
      case EnumBmEntityType.supplierManufacturerCommunicationLogEntry:
        supplierManufacturerCommunicationLogIds.add(entry.relatedBmEntityId);
        break;
    }
  });

  const [
    tasks,
    logEntries,
    leadLogEntries,
    credits,
    orders,
    supplierManufacturerLogEntries,
    users,
  ] = await Promise.all([
    bmTaskService
      .getBmTasks(api.tenantId, Array.from(taskIds.values()))
      .then((tasks) => toGuidMap(tasks, (o) => o.id)),
    outletCommunicationLogService
      .getOutletLogEntries(
        api.tenantId,
        Array.from(outletCommunicationLogIds.values())
      )
      .then((outletCommunications) =>
        toGuidMap(outletCommunications, (o) => o.id)
      ),
    leadCommunicationLogService
      .getLeadLogEntries(
        api.tenantId,
        Array.from(leadCommunicationLogIds.values())
      )
      .then((leadCommunications) => toGuidMap(leadCommunications, (l) => l.id)),
    creditService
      .getCredits(api.tenantId, Array.from(creditIds.values()))
      .then(
        (credits) =>
          new GuidMap(
            credits.map((credit) => [credit.header.readOnly!.id, credit])
          )
      ),
    orderService
      .getOrders(api.tenantId, Array.from(orderIds.values()), false)
      .then(
        (orders) =>
          new GuidMap(orders.map((order) => [order.header.readOnly!.id, order]))
      ),
    supplierManufacturerCommunicationLogService
      .getSupplierManufacturerEntries(
        api.tenantId,
        Array.from(supplierManufacturerCommunicationLogIds.values())
      )
      .then((supplierCommunications) =>
        toGuidMap(supplierCommunications, (l) => l.id)
      ),
    // Not much benefit to getting specifics here
    userService
      .getAllUsers(api.tenantGroupId)
      .then((users) => toGuidMap(users, (o) => o.id)),
    cache.update(),
  ]);

  const types = toGuidMap(cache.communicationTypes.all, (item) => item.id);
  const subjects = toGuidMap(
    cache.communicationSubjects.all,
    (item) => item.id
  );

  return {
    tasks,
    logEntries,
    leadLogEntries,
    credits,
    orders,
    users,
    types,
    subjects,
    supplierManufacturerLogEntries,
  };
}

function getAssociationForEntity(
  entityType: EnumBmEntityType | null,
  entityId: Guid | null,
  status: EnumLogEntryStatus | null,
  tasks: GuidMap<BmTask>,
  logEntries: GuidMap<OutletCommunicationLogEntry>,
  leadLogEntries: GuidMap<LeadCommunicationLogEntry>,
  supplierManufacturerLogEntries: GuidMap<SupplierManufacturerCommunicationLogEntry>,
  credits: GuidMap<Credit>,
  orders: GuidMap<Order>,
  users: GuidMap<User>,
  types: GuidMap<CommunicationType>,
  subjects: GuidMap<CommunicationSubject>
) {
  if (!entityType || !entityId) {
    return null;
  }

  switch (entityType) {
    case EnumBmEntityType.bmTask:
      return mapTaskToActivityAssociation(tasks.get(entityId)!, users, status);
    case EnumBmEntityType.communicationLogEntry:
      return mapCommunicationLogToAssociation(
        logEntries.get(entityId)!,
        users,
        types,
        subjects
      );
    case EnumBmEntityType.leadCommunicationLogEntry:
      return mapCommunicationLogToAssociation(
        leadLogEntries.get(entityId)!,
        users,
        types,
        subjects
      );
    case EnumBmEntityType.supplierManufacturerCommunicationLogEntry:
      return mapCommunicationLogToAssociation(
        supplierManufacturerLogEntries.get(entityId)!,
        users,
        types,
        subjects
      );
    case EnumBmEntityType.credit: {
      const credit = credits.get(entityId)!;
      return mapCreditToAssociation(credit!, users, status);
    }
    case EnumBmEntityType.order: {
      const order = orders.get(entityId)!;
      return mapOrderToAssociation(order!, users, status);
    }
    default:
      return null;
  }
}

export async function mapActivityLogEntries(
  activityLogEntries: ActivityLogEntry[]
) {
  const {
    tasks,
    logEntries,
    leadLogEntries,
    credits,
    orders,
    users,
    types,
    subjects,
    supplierManufacturerLogEntries,
  } = await fetchActivityLogDependencies(activityLogEntries);

  return activityLogEntries.map((entry): ActivityLogEntryCardType => {
    const status = getActivityLogStatus(entry);
    const association = getAssociationForEntity(
      entry.relatedBmEntityType,
      entry.relatedBmEntityId,
      status,
      tasks,
      logEntries,
      leadLogEntries,
      supplierManufacturerLogEntries,
      credits,
      orders,
      users,
      types,
      subjects
    );

    return {
      timestampString: tenantService.formatDateTime(entry.timestamp),
      taskType: entry.activityLogType.translation,
      entityType: entry.relatedBmEntityType.translation,
      status,
      association,
    };
  });
}

export class EnumLogEntryStatus implements BaseEnumType {
  private static allValues: EnumLogEntryStatus[] = []; // make sure this is top-most

  public static created = new EnumLogEntryStatus(
    0,
    "Created",
    "crm_log_created",
    "primary",
    "brand02"
  );
  public static cancelled = new EnumLogEntryStatus(
    1,
    "Cancelled",
    "crm_log_cancelled",
    "error",
    "errorMedium"
  );
  public static completed = new EnumLogEntryStatus(
    2,
    "Completed",
    "crm_log_completed",
    "success"
  );

  public key: number;
  public value: string;
  public translationKey: string;
  public chipColour: string;
  public chipColourDark?: string;

  private constructor(
    key: number,
    value: string,
    translationKey: string,
    chipColour: string,
    chipColourDark?: string
  ) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    this.chipColour = chipColour;
    this.chipColourDark = chipColourDark;
    EnumLogEntryStatus.allValues.push(this);
  }

  public get translation() {
    return translate(Key.fromString(this.translationKey));
  }

  public static getValues(): EnumLogEntryStatus[] {
    return EnumLogEntryStatus.allValues;
  }

  public static getByKey(key: number): EnumLogEntryStatus {
    const found = EnumLogEntryStatus.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumLogEntryStatus - Key not found: ${key}`);
    }

    return found;
  }
}
